import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { getAllContent } from '../../services/contentService';
import { getTemplates } from '../../services/templates';
import logger from '../../utils/logger';

// Types for content items
export interface ContentItem {
  _id: string;
  name?: string;
  type: string;
  details?: {
    title?: string;
    message?: string;
    description?: string;
  };
  files?: Array<{
    fileName: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface WhatsAppTemplate {
  _id: string;
  name: string;
  category: string;
  status: string;
  components: Array<{
    type: string;
    text?: string;
  }>;
  [key: string]: any;
}

export interface ContentState {
  // All content items (files, pages, messages)
  allContent: ContentItem[];
  
  // WhatsApp templates separately
  whatsAppTemplates: WhatsAppTemplate[];
  
  // Loading states
  loading: {
    content: boolean;
    templates: boolean;
  };
  
  // Error states
  error: {
    content: string | null;
    templates: string | null;
  };
  
  // Cache metadata
  lastFetched: {
    content: string | null;
    templates: string | null;
  };
  
  // Total counts for each type
  counts: {
    file: number;
    page: number;
    message: number;
    whatsapp_template: number;
  };
}

// Initial state
const initialState: ContentState = {
  allContent: [],
  whatsAppTemplates: [],
  loading: {
    content: false,
    templates: false,
  },
  error: {
    content: null,
    templates: null,
  },
  lastFetched: {
    content: null,
    templates: null,
  },
  counts: {
    file: 0,
    page: 0,
    message: 0,
    whatsapp_template: 0,
  },
};

// Async thunk for fetching all content
export const fetchAllContent = createAsyncThunk(
  'content/fetchAllContent',
  async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { lastFetched } = state.whatsapp.content;
      
      // Check if we need to fetch (cache for 5 minutes)
      if (!forceRefresh && lastFetched.content) {
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        const lastFetchTime = new Date(lastFetched.content).getTime();
        
        if (lastFetchTime > fiveMinutesAgo) {
          logger.log('📋 Content: Using cached content (less than 5 minutes old)');
          return { cached: true };
        }
      }
      
      logger.log('📥 Content: Fetching all content from API with page size 500...');
      
      const response = await getAllContent({ 
        page: 1, 
        perPage: 500 
      });
      
      logger.log('📦 Content: Raw API response:', response.data);
      
      if (response.data && response.data.data) {
        const content = response.data.data;
        logger.log('✅ Content: Successfully fetched', content.length, 'items');
        logger.log('📊 Content: Types found:', [...new Set(content.map((item: any) => item.type))]);
        
        return {
          content,
          timestamp: new Date().toISOString() as string,
          total: response.data.total || content.length,
        };
      } else {
        logger.log('⚠️ Content: Unexpected API response format:', response.data);
        return rejectWithValue('Invalid response format');
      }
    } catch (error: any) {
      logger.log('❌ Content: Error fetching content:', error);
      return rejectWithValue(error.message || 'Failed to fetch content');
    }
  }
);

// Async thunk for fetching WhatsApp templates
export const fetchWhatsAppTemplates = createAsyncThunk(
  'content/fetchWhatsAppTemplates',
  async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { lastFetched } = state.whatsapp.content;
      
      // Check if we need to fetch (cache for 5 minutes)
      if (!forceRefresh && lastFetched.templates) {
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        const lastFetchTime = new Date(lastFetched.templates).getTime();
        
        if (lastFetchTime > fiveMinutesAgo) {
          logger.log('📋 Templates: Using cached templates (less than 5 minutes old)');
          return { cached: true };
        }
      }
      
      logger.log('📥 Templates: Fetching WhatsApp templates with page size 500...');
      
      const response = await getTemplates({ 
        page: 1, 
        perPage: 500 
      });
      
      logger.log('📦 Templates: Raw API response:', response.data);
      
      if (response?.data?.data) {
        // Filter for approved templates only
        const approvedTemplates = response.data.data.filter(
          (template: WhatsAppTemplate) => template.status === 'APPROVED'
        );
        
        logger.log('✅ Templates: Successfully fetched', approvedTemplates.length, 'approved templates');
        
        return {
          templates: approvedTemplates,
          timestamp: new Date().toISOString() as string,
          total: response.data.total || approvedTemplates.length,
        };
      } else {
        logger.log('⚠️ Templates: No templates found in response');
        return {
          templates: [],
          timestamp: new Date().toISOString() as string,
          total: 0,
        };
      }
    } catch (error: any) {
      logger.log('❌ Templates: Error fetching templates:', error);
      return rejectWithValue(error.message || 'Failed to fetch templates');
    }
  }
);

// Create the slice
const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    // Clear all content and force refresh
    clearContentCache: (state) => {
      logger.log('🧹 Content: Clearing cache');
      state.allContent = [];
      state.whatsAppTemplates = [];
      state.lastFetched.content = null;
      state.lastFetched.templates = null;
      state.counts = {
        file: 0,
        page: 0,
        message: 0,
        whatsapp_template: 0,
      };
    },
    
    // Clear errors
    clearContentErrors: (state) => {
      state.error.content = null;
      state.error.templates = null;
    },
    
    // Update counts when content changes
    updateContentCounts: (state) => {
      const counts = {
        file: 0,
        page: 0,
        message: 0,
        whatsapp_template: state.whatsAppTemplates.length,
      };
      
      state.allContent.forEach(item => {
        if (item.type === 'file') counts.file++;
        else if (item.type === 'page') counts.page++;
        else if (item.type === 'message') counts.message++;
      });
      
      state.counts = counts;
      logger.log('📊 Content: Updated counts:', counts);
    },
  },
  extraReducers: (builder) => {
    // Handle fetchAllContent
    builder
      .addCase(fetchAllContent.pending, (state) => {
        state.loading.content = true;
        state.error.content = null;
      })
      .addCase(fetchAllContent.fulfilled, (state, action) => {
        state.loading.content = false;
        
        if (action.payload.cached) {
          logger.log('📋 Content: Using cached data');
          return;
        }
        
        if (action.payload.content && action.payload.timestamp) {
          state.allContent = action.payload.content;
          state.lastFetched.content = action.payload.timestamp;
          
          // Update counts
          contentSlice.caseReducers.updateContentCounts(state);
        }
      })
      .addCase(fetchAllContent.rejected, (state, action) => {
        state.loading.content = false;
        state.error.content = (action.payload ? String(action.payload) : null) || 'Unknown error';
      })
      
      // Handle fetchWhatsAppTemplates
      .addCase(fetchWhatsAppTemplates.pending, (state) => {
        state.loading.templates = true;
        state.error.templates = null;
      })
      .addCase(fetchWhatsAppTemplates.fulfilled, (state, action) => {
        state.loading.templates = false;
        
        if (action.payload.cached) {
          logger.log('📋 Templates: Using cached data');
          return;
        }
        
        if (action.payload.templates && action.payload.timestamp) {
          state.whatsAppTemplates = action.payload.templates;
          state.lastFetched.templates = action.payload.timestamp;
          
          // Update counts
          contentSlice.caseReducers.updateContentCounts(state);
        }
      })
      .addCase(fetchWhatsAppTemplates.rejected, (state, action) => {
        state.loading.templates = false;
        state.error.templates = (action.payload ? String(action.payload) : null) || 'Unknown error';
      });
  },
});

// Export actions
export const {
  clearContentCache,
  clearContentErrors,
  updateContentCounts,
} = contentSlice.actions;

// Selectors
export const selectAllContent = (state: RootState) => state.whatsapp.content.allContent;
export const selectWhatsAppTemplates = (state: RootState) => state.whatsapp.content.whatsAppTemplates;

export const selectContentByType = (type: string) => (state: RootState) =>
  state.whatsapp.content.allContent.filter(item => item.type === type);

export const selectContentLoading = (state: RootState) => state.whatsapp.content.loading;
export const selectContentErrors = (state: RootState) => state.whatsapp.content.error;
export const selectContentCounts = (state: RootState) => state.whatsapp.content.counts;
export const selectContentLastFetched = (state: RootState) => state.whatsapp.content.lastFetched;

// Combined selector for filtered content based on tab
export const selectFilteredContent = (activeTab: string, searchInput: string = '') => (state: RootState) => {
  let filtered: (ContentItem | WhatsAppTemplate)[] = [];
  
  if (activeTab === 'whatsapp_template') {
    filtered = state.whatsapp.content.whatsAppTemplates;
  } else {
    filtered = state.whatsapp.content.allContent.filter(item => item.type === activeTab);
  }
  
  // Apply search filter
  if (searchInput.trim()) {
    const searchTerm = searchInput.toLowerCase();
    filtered = filtered.filter(item => {
      const name = item.name || item.details?.title || '';
      const description = item.details?.description || item.details?.message || '';
      return name.toLowerCase().includes(searchTerm) || 
             description.toLowerCase().includes(searchTerm);
    });
  }
  
  return filtered;
};

// Export reducer
export default contentSlice.reducer;